import { CoreBox, CoreTooltip } from '@youscience/core';

import ActionButton from '@components/ActionButton';
import { BreadcrumbsLinks } from '@interfaces/location';

import { getSchoolDetailsPath } from '@constants/ROUTES';

import { SchoolInfographicItem } from '@components/SchoolInfographicItem';
import { EllipsisTypography } from '@components/EllipsisTypography';
import { UtahBadge } from '@components/UtahBadge';

import HatIcon from '@images/hat.svg?react';
import EnrollmentSizeIcon from '@images/enrollement-size.svg?react';
import CostIcon from '@images/cost.svg?react';
import LocationIcon from '@images/location.svg?react';
import HeartIcon from '@images/heart.svg?react';
import FilledHeartIcon from '@images/heart-filled.svg?react';
import ChevronRight from '@images/chevron-right.svg?react';
import NotesIcon from '@images/notes.svg?react';
import defaultSchoolPhoto from '@images/default-school-details.png';

import {
  sxStyles,
  StyledReducedSchoolCard,
  StyledSchoolPhoto,
  StyledSchoolDetailsContainer,
  StyledNotes,
  StyledUtahBadgeContainer,
  StyledAddressContainer,
  StyledDistance,
  StyledActionsContainer,
  StyledMoreLink,
} from './ReducedSchoolCard.styles';

interface ReducedSchoolCardProps {
  id?: number;
  title: string;
  schoolType: string;
  enrollmentSize: string;
  costPerYear: string | number;
  pictureUrl: string;
  address: string;
  isUshe?: boolean;
  distanceLabel?: string;
  isSchoolFavorite?: boolean;
  addToFavoritesButtonId?: string;
  isHideActions?: boolean;
  isFluid?: boolean;
  onToggleAddToFavorites?: () => void;
  onOpenSurveyNotes?: () => void;
  cameFrom?: { from: BreadcrumbsLinks[] };
  photoPreview?: JSX.Element;
}

export const ReducedSchoolCard = (props: ReducedSchoolCardProps) => {
  const {
    id,
    title,
    schoolType,
    enrollmentSize,
    costPerYear,
    pictureUrl = '',
    address,
    isUshe,
    distanceLabel,
    isSchoolFavorite,
    addToFavoritesButtonId,
    isHideActions,
    isFluid,
    onToggleAddToFavorites,
    onOpenSurveyNotes,
    cameFrom,
    photoPreview,
  } = props;

  return (
    <StyledReducedSchoolCard data-testid="reducedSchoolCard" isFluid={isFluid}>
      <CoreBox sx={sxStyles.infographicContainer}>
        <SchoolInfographicItem icon={<HatIcon />} title="School type" value={schoolType} />

        <SchoolInfographicItem icon={<EnrollmentSizeIcon />} title="Enrollment size" value={enrollmentSize} />

        <SchoolInfographicItem icon={<CostIcon />} title="Cost per year" value={costPerYear} />
      </CoreBox>

      <StyledSchoolPhoto>
        {photoPreview || <img src={pictureUrl || defaultSchoolPhoto} alt="school view" />}

        {isSchoolFavorite && (
          <StyledNotes data-testid="notesIcon" onClick={onOpenSurveyNotes}>
            <NotesIcon />
          </StyledNotes>
        )}
      </StyledSchoolPhoto>

      <StyledSchoolDetailsContainer>
        <CoreTooltip title={title}>
          <EllipsisTypography
            data-testid="schoolTitle"
            stringCount={2}
            sx={sxStyles.schoolTitle}
            component="div"
            variant="h5"
          >
            {title}
          </EllipsisTypography>
        </CoreTooltip>

        {isUshe && (
          <StyledUtahBadgeContainer>
            <UtahBadge variant="ushe" />
          </StyledUtahBadgeContainer>
        )}

        <StyledAddressContainer>
          <CoreBox>
            <LocationIcon />
          </CoreBox>

          <CoreTooltip title={address}>
            <EllipsisTypography stringCount={1} variant="caption">
              {address}
            </EllipsisTypography>
          </CoreTooltip>

          {distanceLabel && (
            <StyledDistance data-testid="schoolDistance" variant="caption">
              {` · ${distanceLabel}`}
            </StyledDistance>
          )}
        </StyledAddressContainer>
      </StyledSchoolDetailsContainer>

      {!isHideActions && (
        <StyledActionsContainer>
          <ActionButton
            data-testid="favoriteButton"
            id={addToFavoritesButtonId}
            sx={sxStyles.favorite}
            variant="text"
            type="button"
            onClick={onToggleAddToFavorites}
          >
            {isSchoolFavorite ? <FilledHeartIcon /> : <HeartIcon />}
          </ActionButton>

          <StyledMoreLink to={getSchoolDetailsPath(id)} state={cameFrom}>
            More
            <ChevronRight />
          </StyledMoreLink>
        </StyledActionsContainer>
      )}
    </StyledReducedSchoolCard>
  );
};
